import { useTranslations } from "@jugl-web/utils";
import { useCallback, type ReactNode } from "react";
import type { VoiceModeFatalErrorReason } from "../services/VoiceModeSession";

export interface VoiceModeFatalErrorAlert {
  content: ReactNode;
  variant: "info" | "error";
}

/**
 * Maps a voice-mode fatal reason to the alert shown to the user
 * (localized message + alert variant).
 */
export const useVoiceModeFatalErrorAlert = () => {
  const { t } = useTranslations();

  const getVoiceModeFatalErrorAlert = useCallback(
    (reason: VoiceModeFatalErrorReason): VoiceModeFatalErrorAlert => {
      switch (reason) {
        case "microphone-permission-denied":
          return {
            content: t({
              id: "chat-bot-ui.voice-mode-error.microphone-permission-denied",
              defaultMessage: "Microphone access is required for voice mode",
            }),
            variant: "error",
          };
        case "speech-recognition-unsupported":
          return {
            content: t({
              id: "chat-bot-ui.voice-mode-error.speech-recognition-unsupported",
              defaultMessage:
                "Speech recognition is not supported in this browser",
            }),
            variant: "error",
          };
        case "speech-recognition-service-not-allowed":
          return {
            content: t({
              id: "chat-bot-ui.voice-mode-error.speech-recognition-service-not-allowed",
              defaultMessage:
                "Speech recognition is not allowed in this browser",
            }),
            variant: "error",
          };
        case "channel-join-failed":
          return {
            content: t({
              id: "chat-bot-ui.voice-mode-error.channel-join-failed",
              defaultMessage: "Could not connect to voice mode",
            }),
            variant: "error",
          };
        case "quota-exceeded":
          return {
            content: t(
              {
                id: "chat-bot-ui.voice-mode-error.quota-exceeded",
                defaultMessage:
                  "AI Agent is not available at the moment 😴{br}Please try again later",
              },
              { br: <br /> }
            ),
            variant: "info",
          };
        case "network":
          return {
            content: t({
              id: "chat-bot-ui.voice-mode-error.network",
              defaultMessage: "Network error occurred while in voice mode",
            }),
            variant: "error",
          };
        default:
          return {
            content: t({
              id: "chat-bot-ui.voice-mode-error.unknown",
              defaultMessage:
                "⚠️ Something went wrong. Please try again later.",
            }),
            variant: "error",
          };
      }
    },
    [t]
  );

  return { getVoiceModeFatalErrorAlert };
};
